/**
 * Errors that only say "this device has no connection right now". Our code did not fail and the Firebase SDK retries by itself,
 * so reporting them re-opens Sentry issues for nothing (OFFICEHUB-CLIENT-6V, 6A, 7T, 7V: 12 events in 30 days, 10 with no
 * signed-in user). ONE definition, used by both paths that reach Sentry (the console forwarder and a caller's own catch both end in captureError()).
 *
 * ONLY WHILE THE DEVICE REPORTS OFFLINE (navigator.onLine === false). The same texts also appear ONLINE when googleapis is blocked, a CSP/CORS
 * regression ships, or Google has an outage: that is a break for every user and must keep reporting. So the match alone never drops anything.
 * A dropped report leaves a Sentry breadcrumb (category 'offline-noise', the pattern name) so a later real event carries the trail.
 *
 * Deliberately NOT here: a status-0 failure of OUR callable (`Network error: POST ...`). That one is how a real server
 * outage looks from the browser (the 2026-10-22 instance-start failures reached users as exactly that), so it keeps reporting.
 * Each pattern below matches the SDK's own wording only: a refusal, a missing document or a server HTTP error does not match.
 */
const DEVICE_OFFLINE_PATTERNS: ReadonlyArray<{ readonly name: string; readonly re: RegExp }> = [
  // Firestore getDoc/getDocs while the SDK is offline (CLIENT-6V from useGeocode, CLIENT-6A from the profile fetch)
  { name: 'firestore-client-offline', re: /Failed to get documents? because the client is offline/i },
  // Remote Config fetch with no network (CLIENT-7T)
  { name: 'remote-config-no-network', re: /Remote Config: Fetch client failed to connect to a network/i },
  // Auth token refresh / sign-in with no network (CLIENT-7V)
  { name: 'auth-network-request-failed', re: /auth\/network-request-failed/i },
  // The Firestore SDK's own "went offline" notice (CLIENT-5X, CLIENT-5D; was a console-only entry, #5774)
  { name: 'firestore-backend-unreachable', re: /Could not reach Cloud Firestore backend/i },
];

/** The name of the offline pattern the text matches, or null. Message-only and unanchored (named limit): a longer message that merely contains the phrase matches. */
export function matchDeviceOfflinePattern(text: string): string | null {
  return DEVICE_OFFLINE_PATTERNS.find((p) => p.re.test(text))?.name ?? null;
}

/** True only when the browser says the device is offline (a missing navigator or an undefined flag is NOT offline: report as before). */
export function deviceReportsOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** True when the text is a device-has-no-connection line AND the device reports offline. */
export function isDeviceOfflineNoise(text: string, offline: boolean = deviceReportsOffline()): boolean {
  return offline && matchDeviceOfflinePattern(text) !== null;
}
